import {
  getPreviousQuestionId,
  getQuestionIdsForNavigation,
  pollHasLogicRules,
  selectGroupFirstQuestion,
} from 'components-machine';
import { ReactNode } from 'react';
import React from 'react';
import { RATING_TYPE, SchemaEngine } from 'schema';
import type { Poll, Set as PollSet, Question, QuestionDeck } from 'schema/poll';
import { AnswerState, ICState } from 'schema/state';
import { QuestionProps } from 'src/types/poll.domain';
import intl from '../../locale';
import { Progress } from '../../types/domain';

export type WrapperProps = {
  title: string | null;
  children: ReactNode;
  hasTextField: boolean;
  hasMultipleAnswerQuestion: boolean;
  isAllQuestionsOptional: boolean;
  isAllRequiredQuestionsAnswered: boolean;
  isAllQuestionsAnswered: boolean;
  isSomeQuestionsAnswered: boolean;
  isSequentialDisplayGroup: boolean | undefined;
  set?: PollSet;
  progress: Progress;
  progressBar: NonNullable<Poll['progressBar']>;
  textFieldDisclaimer: Poll['textFieldDisclaimer'];
  isWebview: boolean;
  autoCommit: boolean;
  hasWelcome: boolean;
  isLoading: boolean;
  showBackButton: boolean;
  onBack: VoidFunction;
  onClose: VoidFunction;
  onTryAnswer: VoidFunction;
  onAnswer: VoidFunction;
  onNext: VoidFunction;
  onSkip: VoidFunction;
  systemButtonText: { next?: string; answer?: string; skip?: string } | undefined;
  alignSurvey: 'left' | 'center';
  answeredIds?: string[];
  answeredOthers?: string;
};

export function getProgress({ poll, icState }: { poll: Poll; icState: ICState }) {
  const progressBar = getProgressBar(poll);
  const withSequentialDisplay = progressBar === 'percent';

  let currentQuestionId: string | null = null;
  const previousQuestionId = getPreviousQuestionId(poll, icState, { withSequentialDisplay });
  const questionIds = getQuestionIdsForNavigation(poll, icState, { withSequentialDisplay });

  if (icState.groupId) {
    const group = poll.groups.find(group => group.id === icState.groupId);
    const hasSequentialDisplay = !!group && !!group.data.hasSequentialDisplay;
    const lastQuestionOnStack = icState.answersStack[icState.answersStack.length - 1];

    const currentQuestion =
      hasSequentialDisplay && withSequentialDisplay
        ? lastQuestionOnStack
        : selectGroupFirstQuestion(poll, icState.groupId);

    currentQuestionId = currentQuestion?.id ?? null;
  } else {
    currentQuestionId = icState.questionId ?? null;
  }

  const onFinal = !!icState.finalId;
  const currentQuestionIndex = currentQuestionId ? questionIds.indexOf(currentQuestionId) : -1;
  const previousQuestionIndex = previousQuestionId ? questionIds.indexOf(previousQuestionId) : -1;
  const questionCount = questionIds.length;

  return { currentQuestionIndex, previousQuestionIndex, questionCount, onFinal };
}

export const getProgressBar = (poll: Poll): NonNullable<Poll['progressBar']> => {
  const linearFlowProgressBars = new Set<Poll['progressBar']>(['numbers'] satisfies Poll['progressBar'][]);

  const { progressBar } = poll;
  const hasLogicRules = pollHasLogicRules(poll);

  if (!progressBar || (hasLogicRules && linearFlowProgressBars.has(progressBar))) {
    return 'invisible';
  }

  return progressBar;
};

export type MayHaveAnswerCountLimit =
  | { hasAnswerCountLimit?: undefined | false }
  | { hasAnswerCountLimit: true; answerCountMaximum?: number };
export function getFooterErrorHint(
  question: Wide<MayHaveAnswerCountLimit>,
  { answeredIds = [] }: { answeredIds?: string[] },
) {
  if (question.hasAnswerCountLimit && answeredIds.length >= (question.answerCountMaximum ?? Infinity)) {
    return intl.formatMessage({ id: 'System.MaxAnswersCountReached' });
  }
  return undefined;
}

export function getFooterHint(textFieldDisclaimer: Poll['textFieldDisclaimer'], hasTextField: boolean) {
  return hasTextField ? textFieldDisclaimer : undefined;
}
export type GroupQuestionWrapperProps = Pick<QuestionProps, 'question' | 'alignSurvey'> & {
  children: React.ReactNode;
  answerState?: AnswerState;
  questionId: string;
  isLast: boolean;
  isFirstPollQuestion: boolean;
  questionIndexAtGroup: number;
  deck?: QuestionDeck['data'] | null;
};
export type OuterGroupWrapperProps = WrapperProps & {
  relevantQuestions: Question[];
  children: ReactNode;
  isLoading: boolean;
  progress: Progress;
  group: { id: string; name?: string; text?: string; autoCommit?: boolean };
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onAnswer: () => void;
  onClose: () => void;
  systemButtonText: Question['data']['systemButtonText'];
  deck?: QuestionDeck['data'];
};

const TYPES_WITH_MINIMUM = ['POLL_RATING_DIGITS', 'POLL_RATING_SLIDER', 'MATRIX_POLL_RATING_DIGITS'];

export function getColumnsCount(
  schemaEngine: SchemaEngine,
  question: {
    type: string;
    answers?: unknown[];
    minimumValue?: number;
    maximumValue?: number;
    hasNotApplicable?: boolean;
  },
) {
  const { type, answers, minimumValue, maximumValue, hasNotApplicable } = question;

  if (schemaEngine.hasTrait(type, RATING_TYPE)) {
    const count = (maximumValue as number) - (TYPES_WITH_MINIMUM.includes(type) ? minimumValue || 0 : 1) + 1;
    return count + ((answers as { exclusiveAnswer?: boolean }[])?.filter(it => it.exclusiveAnswer)?.length ?? 0);
  }

  return (answers?.length ?? 0) + (hasNotApplicable ? 1 : 0);
}
